<template>
  <div
    v-if="type === 'clickable'"
    role="button"
    :class="classes"
    :style="style"
    @click="click"
  >
    <div
      class="label"
      :class="textClass"
    >
      <template v-if="label">
        {{ label }}
      </template>
      <template v-else>
        <!--
          SLOT: default
          Default content slot of the chip.
          Label that will only show if the label prop is empty.
        -->
        <slot />
      </template>
    </div>
  </div>
  <div
    v-else
    :class="classes"
    :style="style"
    @click="click"
  >
    <div
      class="label"
      :class="textClass"
    >
      <template v-if="label">
        {{ label }}
      </template>
      <template v-else>
        <!--
          SLOT: default
          Default content slot of the chip.
          Label that will only show if the label prop is empty.
        -->
        <slot />
      </template>
    </div>
    <button
      v-if="type === 'dismissible'"
      class="dismiss-btn"
      @click="dismiss"
    >
      <span>
        <icon
          type="light"
          icon="x"
          :size="(size === 'small') ? '8px' : '16px'"
        />
      </span>
    </button>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, reactive} from 'vue';
import Icon from "../Icon.vue";

export default defineComponent({
  name: "Chip",
  components: {Icon},
  props: {
    /**
     * PROP: type
     * The type of the chip.
     * Options: 'informational', 'dismissible', 'clickable'
     **/
    type: {
      type: String,
      default: "informational",
      validator: (value: string) => [
        'informational',
        'dismissible',
        'clickable'
      ].includes(value)
    },
    /**
     * PROP: status
     * Changes background color of chip depending on status.
     * Only used with informational chips (not clickable or dismissible)
     * Options: 'default', 'success', 'caution', 'danger'
     **/
    status: {
      type: String,
      default: "default",
      validator: (value: string) => [
        'default',
        'success',
        'caution',
        'danger'
      ].includes(value)
    },
    /**
     * PROP: intensity
     * Changes background color and text of chip depending on intensity.
     * Only used with informational chips (not clickable or dismissible)
     * Options: 'bold', 'subtle'
     **/
    intensity: {
      type: String,
      default: "bold",
      validator: (value: string) => [
        'bold',
        'subtle'
      ].includes(value)
    },
    /**
     * PROP: size
     * Size of the chip.
     * Informational Chips use small, medium & large.
     * Interactive chips use medium and large.
     * Options: 'small', 'medium', 'large'
     **/
    size: {
      type: String,
      default: "medium",
      validator: (value: string) => [
        'large',
        'medium',
        'small'
      ].includes(value)
    },
    /**
     * PROP: label
     * The label for the chip.
     * Default slot only shows if label prop is not set.
     **/
    label: {
      type: String,
      default: ""
    },
  },
  /**
   * EMITS:
   * 1. click
   *    - Triggers when a clickable chip is clicked.
   * 1. dismiss
   *    - Triggers when a dismissible chip is clicked.
   **/
  emits: ['click', 'dismiss'],
  setup(props) {
     
    props = reactive(props);

    return {
      classes: computed(() => ({
        'chip': true,
        'chip-wrapper': true,
        'chip--informational': props.type === 'informational',
        'chip--dismissible': props.type === 'dismissible',
        'chip--clickable': props.type === 'clickable',
        [`chip--${props.status}`]: props.status,
        [`chip--${props.size}`]: props.size,
        [`chip--${props.intensity}`]: props.intensity,
      })),
      style: computed(() => ({})),
    };
  },
  computed: {
    // Computed type prop for label text
    textClass() {
      switch (this.size) {
        case "large":
          return "body-1";
        case "small":
          if (this.type === 'informational') {
            return "caption";
          }
          return "body-2";
        default:
          return "body-2";
      }
    }
  },
  methods: {
    dismiss() {
      if (this.type === 'dismissible') {
        this.$emit('dismiss');
      }
    },
    click() {
      if (this.type === 'clickable') {
        this.$emit('click');
      }
    }
  },
});
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';


.chip-wrapper {
  border-radius: $radius-sm;
  display: inline-flex;
  align-items: center;
  height: 24px;

  .label {
    text-transform: capitalize;
  }

  /* STANDARD INFORMATIONAL CHIP - DEFAULT */
  &.chip--informational {
    /* INFORMATIONAL CHIP SIZE STYLES */
    &.chip--large {
      height: 24px;
      @apply bg-default_secondary;
      border-radius: $radius-full;
      padding: 0 $space-sm 0 $space-sm;
      .label {
        margin: 0 $space-md;
      }
    }

    &.chip--medium {
      height: 20px;

      .label {
        margin: 0 $space-sm;
      }
    }

    &.chip--small {
      height: 16px;

      .label {
        margin: 0 $space-xs;
      }
    }

    /* INFORMATIONAL CHIP STATUS STYLES */
    &.chip--bold {
      color: white;
      @apply bg-default_secondary;

      &.chip--success {
        @apply bg-default_primary;
      }

      &.chip--caution {
        @apply bg-amber-600;
      }

      &.chip--danger {
        @apply bg-red-700;
      }
    }

    &.chip--subtle {
      color: white;
      @apply bg-blue-600;

      &.chip--success {
        @apply bg-default_primary;
      }

      &.chip--caution {
        @apply bg-amber-600;
      }

      &.chip--danger {
        @apply bg-red-700;
      }
    }

  }

  /* DISMISSIBLE CHIP */
  &.chip--dismissible {
    height: 24px;
    @apply bg-default_secondary;
    border-radius: $radius-full;
    padding: 0 $space-sm 0 $space-lg;

    .label {
      color: white;
      margin-right: $space-sm;
    }

    .dismiss-btn {
      display: flex;
      justify-content: center;
      align-items: center;
      @apply bg-white border border-default_secondary_dark;
      padding: 0;
      margin: 0;
      border-radius: $radius-full;
      height: 18px;
      width: 18px;

      > span {
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
        border-radius: $radius-full;

        ::v-deep(.icon) svg path {
          @apply fill-default_secondary_dark;
        }
      }

      &:hover {
        background-color: white;

        > span {
          @apply bg-default_tertiary;
        }
      }

      &:focus, &:focus-visible {
      }
    }

    &.chip--large {
      height: 32px;

      .dismiss-btn {
        height: 22px;
        width: 22px;
      }
    }
  }

  /* CLICKABLE CHIP */
  &.chip--clickable {
    height: 24px;
    @apply bg-blue-600;
    border-radius: $radius-full;
    padding: 0;
    cursor: pointer;
    border: 1px solid transparent;
    box-sizing: border-box;
    background-clip: padding-box;

    .label {
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 $space-lg;
      border-radius: $radius-full;
      color: white;
    }

    &:hover .label {
      @apply bg-blue-900;
    }

    //&:focus {
    //  outline: 2px solid $chip-clickable-outline;
    //  border: 1px solid $chip-clickable-border;
    //}
    &.chip--large {
      height: 32px;
    }
  }
}

</style>
